import { ColumnDef } from '@tanstack/react-table';
import { t } from 'i18next';

import { EmptyValue } from '@/components/atoms/EmptyValue/EmptyValue';
import { RowActionIndicator } from '@/components/atoms/RowActionIndicator/RowActionIndicator';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { SortableHeader } from '@/components/ui/table/sortable-header';
import type { ScheduleCadence, SimulationRun } from '@/modules/simulations/types';
import {
  CADENCE_LABEL_KEYS,
  RUN_STATUS_BADGE_FLAVOUR,
  RUN_STATUS_TRANSLATION_KEYS,
} from '@/modules/simulations/types';
import { formatDurationSeconds } from '@/modules/simulations/utils';
import { COLUMN_PRESETS } from '@/util/constants';
import { formatDate } from '@/util/dateUtils';
import { ratioToPercent } from '@/util/numbers';

import { ActionsCell } from './ActionsCell';
import { TriggeredByCell } from './TriggeredByCell';

interface ColumnsProps {
  onStop: (runId: string) => void;
  onDelete: (runId: string) => void;
  getCadence: (run: SimulationRun) => ScheduleCadence | undefined;
}

const formatRunDate = (run: SimulationRun, cadence: ScheduleCadence | undefined) => {
  if (!run.started_at) {
    return null;
  }
  if (cadence) {
    return t('simulations.run_date_with_cadence', {
      0: t(CADENCE_LABEL_KEYS[cadence]),
      1: formatDate(run.started_at, 'MMM d'),
    });
  }
  return formatDate(run.started_at, 'MMM d, h:mm a');
};

export const columns = ({
  onStop,
  onDelete,
  getCadence,
}: ColumnsProps): ColumnDef<SimulationRun>[] => [
  {
    id: 'date',
    accessorKey: 'started_at',
    ...COLUMN_PRESETS.LG,
    meta: { preset: 'LG' as const, className: 'pl-4' },
    enableHiding: false,
    header: ({ column }) => (
      <SortableHeader column={column}>{t('simulations.date')}</SortableHeader>
    ),
    cell: ({ row }) => {
      const label = formatRunDate(row.original, getCadence(row.original));
      return (
        <div className="flex items-center gap-3">
          {label ? <span className="min-w-0 truncate font-medium">{label}</span> : <EmptyValue />}
          <RowActionIndicator action="view" />
        </div>
      );
    },
  },
  {
    id: 'status',
    accessorKey: 'status',
    ...COLUMN_PRESETS.MD,
    size: 120,
    maxSize: 144,
    meta: { preset: 'MD' as const, label: 'simulations.status' },
    header: ({ column }) => (
      <SortableHeader column={column}>{t('simulations.status')}</SortableHeader>
    ),
    cell: ({ row }) => {
      const { status } = row.original;
      const flavour = RUN_STATUS_BADGE_FLAVOUR[status];
      return (
        <Badge type="secondary" flavour={flavour as 'informational'} rounded={false}>
          {t(RUN_STATUS_TRANSLATION_KEYS[status] ?? status)}
        </Badge>
      );
    },
  },
  {
    id: 'success_rate',
    accessorKey: 'success_rate',
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD' as const, label: 'simulations.success_rate' },
    header: ({ column }) => (
      <SortableHeader column={column}>{t('simulations.success_rate')}</SortableHeader>
    ),
    cell: ({ row }) => {
      const pct = ratioToPercent(row.original.success_rate);
      return (
        <div className="flex items-center gap-2">
          <Progress value={pct} className="h-2 w-16" />
          <span className="text-sm">{pct}%</span>
        </div>
      );
    },
  },
  {
    id: 'simulations',
    accessorKey: 'total_count',
    ...COLUMN_PRESETS.MD,
    size: 120,
    maxSize: 144,
    meta: { preset: 'MD' as const, label: 'simulations.simulations_count' },
    header: ({ column }) => (
      <SortableHeader column={column}>{t('simulations.simulations_count')}</SortableHeader>
    ),
    cell: ({ row }) => <span>{row.original.total_count}</span>,
  },
  {
    id: 'fails',
    accessorKey: 'fail_count',
    ...COLUMN_PRESETS.MD,
    size: 120,
    maxSize: 144,
    meta: { preset: 'MD' as const, label: 'simulations.fails' },
    header: ({ column }) => (
      <SortableHeader column={column}>{t('simulations.fails')}</SortableHeader>
    ),
    cell: ({ row }) => {
      const failCount = row.original.fail_count;
      if (failCount === 0) {
        return <EmptyValue />;
      }
      return <span>{failCount}</span>;
    },
  },
  {
    id: 'duration',
    accessorKey: 'duration_seconds',
    ...COLUMN_PRESETS.MD,
    size: 120,
    maxSize: 144,
    meta: { preset: 'MD' as const, label: 'simulations.duration' },
    header: ({ column }) => (
      <SortableHeader column={column}>{t('simulations.duration')}</SortableHeader>
    ),
    cell: ({ row }) => {
      const seconds = row.original.duration_seconds;
      if (seconds === null) {
        return <EmptyValue />;
      }
      return <span>{formatDurationSeconds(seconds)}</span>;
    },
  },
  {
    id: 'triggered_by',
    accessorKey: 'triggered_by',
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD' as const, label: 'simulations.triggered_by' },
    enableSorting: false,
    header: () => <span>{t('simulations.triggered_by')}</span>,
    cell: ({ row }) => <TriggeredByCell run={row.original} />,
  },
  {
    id: 'actions',
    ...COLUMN_PRESETS.SM,
    meta: { preset: 'SM' as const },
    enableHiding: false,
    enableSorting: false,
    cell: ({ row }) => <ActionsCell run={row.original} onStop={onStop} onDelete={onDelete} />,
  },
];
